import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './SurveyChat.css';
import './SurveyThemes.css';
import { useAuth } from "../AuthContext";
import AnalyticsService from '../services/AnalyticsService'; // Import analytics service
import ReactMarkdown from 'react-markdown'; // Import for Markdown rendering

const API_URL = process.env.REACT_APP_API_URL;

const CelebrationEffect = () => {
    const [showCelebration, setShowCelebration] = useState(true);

    useEffect(() => {
        // Generate confetti pieces
        const confettiContainer = document.querySelector('.celebration-container');
        if (confettiContainer) {
            for (let i = 0; i < 80; i++) {
                setTimeout(() => {
                    const confetti = document.createElement('div');
                    confetti.className = `confetti confetti-color-${(i % 7) + 1}`;
                    confetti.style.left = `${Math.random() * 100}%`;
                    confetti.style.animationDelay = `${Math.random() * 0.5}s`;
                    confetti.style.transform = `rotate(${Math.random() * 360}deg)`;
                    confettiContainer.appendChild(confetti);

                    // Remove confetti after animation
                    setTimeout(() => confetti.remove(), 6000);
                }, i * 30);
            }
        }

        // Hide celebration message after 5 seconds
        const timer = setTimeout(() => {
            setShowCelebration(false);
        }, 5000);

        return () => clearTimeout(timer);
    }, []);

    return (
        <>
            <div className="celebration-container" />
            {showCelebration && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="celebration-message"
                >
                    <div className="celebration-emoji">🎉</div>
                    <h2>Congratulations!</h2>
                    <p>You've completed the survey!</p>
                </motion.div>
            )}
        </>
    );
};

const ChatBubble = ({ message, isUser, isFollowUp, assessment, reason, owner, isSkippable }) => {
    const [showAssessment, setShowAssessment] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className={isUser ? 'chat-bubble-user' : 'chat-bubble-bot'}
        >
            <div className="bubble-header">
                {isFollowUp && (
                    <div className="follow-up-header">
                        <span className="follow-up-label">Follow-Up</span>
                        { assessment && owner && (
                            <button
                                onClick={() => setShowAssessment(!showAssessment)}
                                className="assessment-toggle-button"
                            >
                                {showAssessment ? 'Hide Assessment' : 'Show Assessment'}
                            </button>
                        )}
                    </div>
                )}
                {isSkippable && !isUser && !isFollowUp && (
                    <div className="skippable-indicator">
                        <span className="skippable-label">Optional</span>
                    </div>
                )}
            </div>
            <div className="message-content">
                <ReactMarkdown>{message}</ReactMarkdown>
            </div>
            {assessment && showAssessment && (
                <div className={`assessment-indicator assessment-${assessment}`}>
                    <span className="assessment-label">{assessment}</span>
                    {reason && (
                        <div className="assessment-reason">
                            <ReactMarkdown components={{
                                strong: ({node, ...props}) => <span className="md-strong" {...props} />,
                                em: ({node, ...props}) => <span className="md-emphasis" {...props} />
                            }}>
                                {reason.replace(/\*\s\*\*(.*?)\*\*/g, "**$1**").replace(/\*\s\*\*(.*?)\*\*/g, "**$1**")}
                            </ReactMarkdown>
                        </div>
                    )}
                </div>
            )}
        </motion.div>
    );
};

const SurveyChat = ({ survey, theme = 'default' }) => {
    console.log('SurveyChat theme:', theme);
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [mainQuestionIndex, setMainQuestionIndex] = useState(0);
    const [followUpQueue, setFollowUpQueue] = useState([]);
    const [followUpCounts, setFollowUpCounts] = useState({});
    const [responsesWithFollowups, setResponsesWithFollowups] = useState({});
    const [isTyping, setIsTyping] = useState(false);
    const [isSurveyComplete, setIsSurveyComplete] = useState(false);
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [email, setEmail] = useState('');
    const [emailSubmitted, setEmailSubmitted] = useState(false);
    const [showOptions, setShowOptions] = useState(false);
    const [options, setOptions] = useState([]);
    const [showCelebration, setShowCelebration] = useState(false);
    const [showRequestCopyLink, setShowRequestCopyLink] = useState(false);
    const chatContainerRef = useRef(null);
    const inputRef = useRef(null);
    const { user } = useAuth();
    const [owner, setOwner] = useState(false);

    // Analytics tracking refs
    const surveyStartTimeRef = useRef(Date.now());
    const questionStartTimeRef = useRef(Date.now());
    const totalFollowUpsRef = useRef(0);
    
    // Ref to track if input should maintain focus
    const shouldMaintainFocus = useRef(true);

    useEffect(() => {
        if (survey && survey.questions.length > 0) {
            // Track survey start
            AnalyticsService.trackSurveyStart(
                survey.shortcode,
                survey.title,
                survey.questions.length
            );

            setMessages([{ id: 1, text: survey.welcomeMessage? survey.welcomeMessage : survey.title, isUser: false }]);
            setTimeout(() => {
                const firstQuestion = survey.questions[0];
                setMessages((prev) => [...prev, {
                    id: 2,
                    text: firstQuestion.questionText,
                    isUser: false,
                    isSkippable: firstQuestion.isSkippable || false,
                    isFollowUp: false,
                    mainQuestion: true,
                    questionIndex: 0
                }]);

                // Track first question view
                AnalyticsService.trackQuestionView(
                    survey.shortcode,
                    firstQuestion._id,
                    firstQuestion.questionText,
                    0,
                    false
                );

                // Reset question timer
                questionStartTimeRef.current = Date.now();
            }, 1000);
        }
    }, [survey]);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
        if (user && survey) {
            setOwner(survey.user_id === user.uid);
        }
    }, [messages, user, survey]);

    useEffect(() => {
        saveData();
    }, [responsesWithFollowups, isSurveyComplete]);

    // Ensure input maintains focus when typing state changes
    useEffect(() => {
        if (!isTyping && !isSurveyComplete && inputRef.current && shouldMaintainFocus.current) {
            inputRef.current.focus();
        }
    }, [isTyping, isSurveyComplete]);

    // Function to fetch options from API
    const fetchOptions = async () => {
        setIsTyping(true);
        
        try {
            // Build questionsAndAnswers from messages array
            let questionsAndAnswers = [];
            let tempQuestion = null;
            
            // Iterate through messages to build Q&A pairs
            messages.forEach((msg, index) => {
                // Skip the initial welcome message
                if (index === 0 && !msg.isUser) {
                    return;
                }
                
                if (!msg.isUser) {
                    // This is a question (either main or follow-up)
                    tempQuestion = msg.text;
                } else if (tempQuestion && msg.isUser) {
                    // This is an answer to the previous question
                    questionsAndAnswers.push({
                        question: tempQuestion,
                        answer: msg.text
                    });
                    tempQuestion = null;
                }
            });
            
            // Add the current unanswered question
            if (tempQuestion) {
                questionsAndAnswers.push({
                    question: tempQuestion,
                    answer: "" // Empty since user hasn't answered yet
                });
            }
            
            const response = await fetch(`${API_URL}/api/questions/sample`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${await user?.getIdToken()}`
                },
                body: JSON.stringify({
                    questionsAndAnswers,
                    surveyContext: survey.context || survey.description || ""
                }),
            });
            
            const result = await response.json();
            setIsTyping(false);
            
            if (result.success && result.data && result.data.length > 0) {
                setOptions(result.data);
                setShowOptions(true);
            }
            
            // Refocus input
            if (inputRef.current) {
                setTimeout(() => inputRef.current.focus(), 100);
            }
        } catch (error) {
            console.error("Error fetching options:", error);
            setIsTyping(false);
            
            // Refocus input
            if (inputRef.current) {
                setTimeout(() => inputRef.current.focus(), 100);
            }
        }
    };

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
            inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
        }
    }, [input]);

    useEffect(() => {
        if (isSurveyComplete) {
            // Track survey completion before submitting
            const totalTime = Date.now() - surveyStartTimeRef.current;
            AnalyticsService.trackSurveyCompletion(
                survey.shortcode,
                survey.questions.length,
                Object.keys(responsesWithFollowups).length,
                totalFollowUpsRef.current,
                totalTime
            );

            handleSubmit();
        }
    }, [isSurveyComplete]);

    // Add window unload tracking for survey abandonment
    useEffect(() => {
        const handleUnload = () => {
            if (!isSurveyComplete && survey) {
                const timeSpent = Date.now() - surveyStartTimeRef.current;
                AnalyticsService.trackSurveyAbandonment(
                    survey.shortcode,
                    mainQuestionIndex,
                    survey.questions.length,
                    Object.keys(responsesWithFollowups).length,
                    timeSpent
                );
            }
        };

        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, [isSurveyComplete, survey, mainQuestionIndex, responsesWithFollowups]);

    const handleSend = async () => {
        if (!input.trim()) return;

        const currentInput = input;
        const currentIndex = mainQuestionIndex;
        const responseTime = Date.now() - questionStartTimeRef.current;
        const currentQuestion = survey.questions[currentIndex];
        const isFollowUpResponse = followUpQueue.length > 0;

        // Hide options box when user submits an answer
        setShowOptions(false);

        // Check if user typed "skip" for a skippable question
        if (currentInput.toLowerCase() === 'skip' && currentQuestion.isSkippable && !isFollowUpResponse) {
            setInput('');
            handleSkipQuestion();
            return;
        }

        // Check if user typed "give me options"
        if (currentInput.toLowerCase() === 'give me options' || currentInput.toLowerCase() === 'options') {
            setInput('');
            fetchOptions();
            return;
        }

        // Track response analytics
        AnalyticsService.trackQuestionResponse(
            survey.shortcode,
            currentQuestion._id,
            currentIndex,
            currentInput.length,
            responseTime,
            isFollowUpResponse
        );

        // Save response based on whether it's a follow-up or main answer
        setResponsesWithFollowups(prev => {
            const currentResponse = prev[currentIndex] || { answerText: '', followups: [] };

            if (isFollowUpResponse) {
                // It's a follow-up response
                return {
                    ...prev,
                    [currentIndex]: {
                        ...currentResponse,
                        followups: [
                            ...currentResponse.followups,
                            {
                                question: messages[messages.length - 1].text,
                                answer: currentInput
                            }
                        ]
                    }
                };
            } else {
                // It's a main answer
                return {
                    ...prev,
                    [currentIndex]: {
                        ...currentResponse,
                        questionText: currentQuestion.questionText,
                        answerText: currentInput,
                        followups: currentResponse.followups
                    }
                };
            }
        });

        setMessages(prev => [...prev, { id: prev.length + 1,
            text: currentInput,
            isFollowUpResponse: isFollowUpResponse,
            isUser: true }]);

        setInput('');
        setIsTyping(true);

        // Reset question timer for next question/follow-up
        questionStartTimeRef.current = Date.now();
        const aiProcessingStartTime = Date.now();

        setTimeout(async () => {
            let botResponse = '';
            const currentFollowUps = followUpCounts[currentIndex] || 0;
            const maxFollowUps = parseInt(currentQuestion.maxFollowUps, 10) || 0;
            const desiredClarity = currentQuestion.desiredClarity || "medium";

            if (currentFollowUps >= maxFollowUps) {
                if (mainQuestionIndex + 1 >= survey.questions.length) {
                    setIsTyping(false);
                    setIsSurveyComplete(true);
                    return;
                } else {
                    const nextQuestion = survey.questions[mainQuestionIndex + 1];
                    botResponse = nextQuestion.questionText;
                    setMessages(prev => [...prev, {
                        id: prev.length + 1,
                        text: botResponse,
                        isUser: false,
                        isSkippable: nextQuestion.isSkippable || false
                    }]);

                    // Track next question view
                    AnalyticsService.trackQuestionView(
                        survey.shortcode,
                        nextQuestion._id,
                        nextQuestion.questionText,
                        mainQuestionIndex + 1,
                        false
                    );

                    setMainQuestionIndex(mainQuestionIndex + 1);
                    setFollowUpQueue([]);
                }
                setIsTyping(false);
                // Refocus input after typing animation
                if (inputRef.current) {
                    setTimeout(() => inputRef.current.focus(), 100);
                }
                return;
            }

            try {
                let state = []
                // Add main question+answer and follow ups to state
                if (responsesWithFollowups[currentIndex] === undefined) {
                    state.push({ question: currentQuestion.questionText, answer: currentInput })
                }else{
                    // Main question
                    state.push({ question:  responsesWithFollowups[currentIndex].questionText, answer: responsesWithFollowups[currentIndex].answerText  })
                    // Past questions
                    responsesWithFollowups[currentIndex].followups.forEach(followup => { state.push({question: followup.question, answer: followup.answer}) })
                    // Current question
                    state.push({ question: messages[messages.length-1].text, answer: currentInput })
                }
                const response = await fetch(`${API_URL}/api/sl-question`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        state: state,
                        shortcode: survey.shortcode,
                        desiredClarity,
                        surveyVersion: survey.metadata.surveyVersion,
                    }),
                });

                const result = await response.json();
                const aiProcessingTime = Date.now() - aiProcessingStartTime;
                setIsTyping(false);
                
                // Refocus input after typing animation
                if (inputRef.current) {
                    setTimeout(() => inputRef.current.focus(), 100);
                }

                if (result.success) {
                    const { assessment, reason, followUpQuestion, isFollowUpGenerated } = result.data;

                    // Track AI's assessment of the response
                    AnalyticsService.trackResponseAssessment(
                        survey.shortcode,
                        currentQuestion._id,
                        currentIndex,
                        assessment,
                        reason
                    );

                    // Modified: Instead of conditionally showing feedback, store it with the message
                    // but only display if showAssessments is true and user is owner

                    if (isFollowUpGenerated) {
                        // Track follow-up question generation
                        AnalyticsService.trackFollowUpGeneration(
                            survey.shortcode,
                            currentQuestion._id,
                            currentIndex,
                            followUpQuestion,
                            currentFollowUps + 1,
                            aiProcessingTime
                        );

                        setMessages(prev => [
                            ...prev,
                            {
                                id: prev.length + 1,
                                text: followUpQuestion,
                                isUser: false,
                                assessment: assessment,
                                questionIndex: currentIndex,
                                reason: reason,
                                isFollowUp: true,
                                mainQuestion: false
                            }
                        ]);

                        // Track follow-up question view
                        AnalyticsService.trackQuestionView(
                            survey.shortcode,
                            `${currentQuestion._id}-followup-${currentFollowUps + 1}`,
                            followUpQuestion,
                            currentIndex,
                            true
                        );

                        setFollowUpQueue(prevQueue => [...prevQueue, followUpQuestion]);
                        setFollowUpCounts(prevCounts => ({
                            ...prevCounts,
                            [currentIndex]: currentFollowUps + 1,
                        }));

                        // Increment total follow-ups counter
                        totalFollowUpsRef.current += 1;
                    } else {
                        if (followUpQueue.length > 0) {
                            setFollowUpQueue([]);
                        }
                        if (mainQuestionIndex + 1 < survey.questions.length) {
                            const nextQuestion = survey.questions[mainQuestionIndex + 1];
                            botResponse = nextQuestion.questionText;
                            setMessages(prev => [...prev, {
                                id: prev.length + 1,
                                text: botResponse,
                                isUser: false,
                                isSkippable: nextQuestion.isSkippable || false,
                                mainQuestion: true,
                                questionIndex: mainQuestionIndex + 1,
                                isFollowUp: false
                            }]);

                            // Track next question view
                            AnalyticsService.trackQuestionView(
                                survey.shortcode,
                                nextQuestion._id,
                                nextQuestion.questionText,
                                mainQuestionIndex + 1,
                                false
                            );

                            setMainQuestionIndex(mainQuestionIndex + 1);
                        } else {
                            setIsSurveyComplete(true);
                            return;
                        }
                    }
                } else {
                    setMessages(prev => [
                        ...prev,
                        { id: prev.length + 1, text: "Error processing your response. Please try again.", isUser: false }
                    ]);
                }
            } catch (error) {
                console.error("Error in handleSend:", error);
                setIsTyping(false);
                
                // Refocus input after error
                if (inputRef.current) {
                    setTimeout(() => inputRef.current.focus(), 100);
                }
                
                setMessages(prev => [
                    ...prev,
                    { id: prev.length + 1, text: "Error processing your response. Please try again.", isUser: false }
                ]);
            }
        }, 1500);
    };

    let saveData = async ()=>  {
        const answers = survey.questions.map((question, index) => ({
            questionId: question._id,
            questionText: question.questionText,
            answerText: responsesWithFollowups[index]?.answerText || "",
            followups: responsesWithFollowups[index]?.followups || []
        }));

        const submissionPayload = {
            answers,
            shortcode: survey.shortcode,
            surveyVersion: survey.metadata.surveyVersion,
            session: survey.session,
            isSubmitted: isSurveyComplete
        };

        try {
            const response = await fetch(`${API_URL}/api/surveys/${survey.shortcode}/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(submissionPayload),
            });

            if (!response.ok) throw new Error("Save Submission failed.");
        } catch (error) {
            console.error("Submission error:", error);
            alert("There was an issue submitting the survey.");
        }
    }

    const handleSubmit = async () => {
        if (Object.keys(responsesWithFollowups).length === 0) {
            console.error("No responses recorded.");
            alert("No responses recorded.");
            return;
        }

        // Show celebration effect
        setShowCelebration(true);
        
        // Hide celebration after 6 seconds
        setTimeout(() => {
            setShowCelebration(false);
        }, 6000);

        // Add thank you message
        setTimeout(() => {
            setMessages(prev => [
                ...prev,
                { id: prev.length + 1, text: "Thank you for completing the survey!", isUser: false }
            ]);
            // Show request copy link after thank you message
            setShowRequestCopyLink(true);
        }, 300);
    };

    // Handle skip current question
    const handleSkipQuestion = () => {
        const currentQuestion = survey.questions[mainQuestionIndex];

        // Only allow skipping if the question is marked as skippable
        if (!currentQuestion.isSkippable) {
            return;
        }

        // Track skipped question in analytics
        AnalyticsService.trackQuestionSkipped(
            survey.shortcode,
            currentQuestion._id,
            mainQuestionIndex
        );

        // Add empty response
        setResponsesWithFollowups(prev => ({
            ...prev,
            [mainQuestionIndex]: {
                answerText: "[SKIPPED]",
                followups: []
            }
        }));

        // Move to next question
        if (mainQuestionIndex + 1 < survey.questions.length) {
            const nextQuestion = survey.questions[mainQuestionIndex + 1];

            setMessages(prev => [
                ...prev,
                {
                    id: prev.length + 1,
                    text: "[Question skipped]",
                    isUser: true
                },
                {
                    id: prev.length + 2,
                    text: nextQuestion.questionText,
                    isUser: false,
                    isSkippable: nextQuestion.isSkippable || false
                }
            ]);

            // Track next question view
            AnalyticsService.trackQuestionView(
                survey.shortcode,
                nextQuestion._id,
                nextQuestion.questionText,
                mainQuestionIndex + 1,
                false
            );

            setMainQuestionIndex(mainQuestionIndex + 1);
            setFollowUpQueue([]);
        } else {
            setMessages(prev => [
                ...prev,
                {
                    id: prev.length + 1,
                    text: "[Question skipped]",
                    isUser: true
                }
            ]);
            setIsSurveyComplete(true);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSend();
        }
    };

    const handleEmailSubmit = async () => {
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            alert('Please enter a valid email address.');
            return;
        }

        try {
            // Create transcript data from messages
            const transcript = messages.map(msg => ({
                text: msg.text,
                isUser: msg.isUser,
                isFollowUp: msg.isFollowUp || false
            }));

            // Send email request to backend
            const response = await fetch(`${API_URL}/api/surveys/${survey.shortcode}/send-transcript`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    transcript,
                    surveyTitle: survey.title,
                    shortcode: survey.shortcode
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to send transcript.");
            }

            setEmailSubmitted(true);
            setMessages(prev => [
                ...prev,
                { id: prev.length + 1, text: `A copy of this conversation has been sent to ${email}.`, isUser: false }
            ]);

            // Track email request in analytics
            AnalyticsService.trackTranscriptRequest(
                survey.shortcode,
                email,
                messages.length
            );

            // Don't navigate away - keep user on survey page
        } catch (error) {
            console.error("Email submission error:", error);
            alert("There was an issue sending the transcript. Please try again.");
        }
    };

    const handleSkipEmail = () => {
        setEmailSubmitted(true);
        // Don't add another thank you - one was already shown

        // Don't navigate away - keep user on survey page
    };

    return (
        <div className="chat-wrapper" data-theme={theme}>
            {/* Celebration Effect */}
            {showCelebration && <CelebrationEffect />}
            
            {/* Logo Header */}
            {survey?.logoUrl && (
                <div className="survey-logo-header">
                    <img 
                        src={survey.logoUrl} 
                        alt="Company Logo" 
                        className="survey-logo"
                        onError={(e) => {
                            // Hide the logo header if image fails to load
                            e.target.parentElement.style.display = 'none';
                        }}
                    />
                </div>
            )}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="chat-container"
                ref={chatContainerRef}
            >
                <div className="chat-messages">
                    {messages.map((msg) => (
                        <ChatBubble
                            key={msg.id}
                            message={msg.text}
                            isUser={msg.isUser}
                            isFollowUp={msg.isFollowUp}
                            assessment={msg.assessment}
                            reason={msg.reason}
                            owner={owner}
                            isSkippable={msg.isSkippable}
                        />
                    ))}
                    {isTyping && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5 }}
                            className="typing-indicator"
                        >
                            <div className="typing-dot"></div>
                            <div className="typing-dot"></div>
                            <div className="typing-dot"></div>
                        </motion.div>
                    )}
                </div>
                {/* Request Copy Link */}
                {showRequestCopyLink && !emailSubmitted && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                        className="request-copy-container"
                    >
                        <button
                            className="request-copy-link"
                            onClick={() => setShowEmailForm(true)}
                        >
                            Click here to request a copy of the survey
                        </button>
                    </motion.div>
                )}
            </motion.div>
            {/* Tip and Options Container */}
            <div className="tip-options-container">
                {/* Always show tip when survey is not complete */}
                {!isSurveyComplete && (
                    <div className="tip-message">
                        Tip: You can type 'give me options' anytime for suggestions to help you answer!
                    </div>
                )}
                
                {/* Show options when triggered */}
                {showOptions && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="options-container"
                    >
                        <div className="options-header">
                            <span>Choose an option:</span>
                            <button 
                                onClick={() => setShowOptions(false)} 
                                className="close-options"
                            >
                                ×
                            </button>
                        </div>
                        <div className="options-list">
                            {options.map((option, index) => (
                                <button
                                    key={index}
                                    className="option-button"
                                    onClick={() => {
                                        setInput(option["answer"]);
                                        setShowOptions(false);
                                    }}
                                >
                                    {option["answer"]}
                                </button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </div>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="chat-input-container"
            >
                <textarea
                    ref={inputRef}
                    className="chat-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={isSurveyComplete ? "Survey completed" : "Your response..."}
                    rows="1"
                    disabled={isTyping || isSurveyComplete}
                    autoFocus={true}
                    inputMode="text"
                    enterKeyHint="send"
                    autoCapitalize="sentences"
                    autoCorrect="on"
                    onBlur={({ target }) => {
                        // Small delay to allow click events to complete before refocusing
                        setTimeout(() => {
                            if (!isSurveyComplete && !isTyping) {
                                target.focus();
                            }
                        }, 0);
                    }}
                />
                <motion.button
                    onClick={isTyping ? null : handleSend}
                    className={`chat-button ${isTyping ? 'processing' : ''}`}
                    disabled={isTyping || isSurveyComplete}
                >
                    {isTyping ? (
                        <span className="button-processing">
            <span className="dot">.</span><span className="dot">.</span><span className="dot">.</span>
        </span>
                    ) : 'Go'}
                </motion.button>

                {/* Skip button - only shown for skippable questions */}
                {survey?.questions[mainQuestionIndex]?.isSkippable && !isTyping && followUpQueue.length === 0 && (
                    <motion.button
                        onClick={handleSkipQuestion}
                        className="skip-button"
                        whileHover={{ scale: 1.05 }}
                    >
                        Skip
                    </motion.button>
                )}
            </motion.div>

            {/* Email form that appears after survey completion */}
            {showEmailForm && !emailSubmitted && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="email-form-container"
                >
                    <div className="email-form">
                        <h3>Would you like a copy of this conversation?</h3>
                        <p>We can email you a transcript of this survey chat.</p>
                        <form className="email-input-container" onSubmit={(e) => { e.preventDefault(); handleEmailSubmit(); }}>
                            <input
                                type="email"
                                placeholder="Your email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="email-input"
                                inputMode="email"
                                autoComplete="email"
                                enterKeyHint="send"
                                autoFocus
                            />
                            <button type="submit" className="email-submit-button">
                                Send Transcript
                            </button>
                        </form>
                        <button onClick={handleSkipEmail} className="skip-email-button">
                            No thanks
                        </button>
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default SurveyChat;